import cn from 'classnames';
import LazyMotionWrapper from 'components/lazy-motion-wrapper';
import { useIsExistingCustomer } from 'hooks/common/use-is-existing-customer';
import debounce from 'lodash/debounce';
import { m, useAnimation, useMotionValue } from 'motion/react';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useRef } from 'react';
import { useHeaderStore } from 'stores/header';
import { stripRegionPathPrefix } from 'utils/routing/strip-region-path-prefix';
import Nav from './nav';
import PromoBar from './promo-bar';
import HeaderTop from './top-header';
import UspSection from './usp-bar';

interface HeaderProps {
  disableScrollHide?: boolean;
  /** Pass true when a countdown PromoBar is rendered above Header in Layout to avoid duplicate rendering */
  hidePromoBar?: boolean;
}

/**
 * Header component. This component is used to render the header of the website.
 *
 * When a countdown PromoBar is active it is rendered in Layout (sticky, above this component).
 * In that case, hidePromoBar=true is passed so PromoBar is not duplicated here.
 * m.header uses top: var(--promo-bar-height, 0px) so the fixed nav sits below the sticky promo bar.
 */
const Header: React.FC<HeaderProps> = ({ disableScrollHide = false, hidePromoBar = false }) => {
  const { isEc, loading } = useIsExistingCustomer();
  const showUspBar = !loading && !isEc;
  const lastPosition = useRef(0);
  const { pathname, query } = useRouter();
  const isCareerPage =
    stripRegionPathPrefix(pathname) === '/pages/[handle]' && query.handle === 'karriere';
  const { setHeaderHidden } = useHeaderStore();

  const scrollY = useMotionValue(0);
  const controls = useAnimation();

  useEffect(() => {
    if (disableScrollHide) {
      controls
        .start({ y: '0%', opacity: 1, transition: { type: 'spring', stiffness: 100, damping: 20 } })
        .then(() => setHeaderHidden(false));
    }
  }, [controls, disableScrollHide, setHeaderHidden]);

  const onScroll = useCallback(() => {
    const previousY = lastPosition.current;
    lastPosition.current = window.scrollY;
    scrollY.set(window.scrollY);

    if (disableScrollHide) {
      controls
        .start({ y: '0%', opacity: 1, transition: { type: 'spring', stiffness: 100, damping: 20 } })
        .then(() => setHeaderHidden(false));
    } else {
      if (window.scrollY === 0 || previousY > window.scrollY) {
        controls
          .start({
            y: '0%',
            opacity: 1,
            transition: { type: 'spring', stiffness: 100, damping: 20 },
          })
          .then(() => setHeaderHidden(false));
      } else {
        controls
          .start({
            y: '-200%',
            opacity: 0,
            transition: { type: 'spring', stiffness: 100, damping: 20 },
          })
          .then(() => setHeaderHidden(true));
      }
    }
  }, [controls, scrollY, disableScrollHide, setHeaderHidden]);

  useEffect(() => {
    if (disableScrollHide) return;
    const debounced = debounce(onScroll, 75);
    window.addEventListener('scroll', debounced);
    return () => window.removeEventListener('scroll', debounced);
  }, [disableScrollHide, onScroll]);

  // Tracks nav height and writes --header-height CSS variable (double-rAF avoids layout thrashing)
  useEffect(() => {
    const headerWrapper = document.getElementById('header');
    if (!headerWrapper) return;

    let animationFrameId: number | null = null;
    const resizeObserver = new ResizeObserver((entries) => {
      if (animationFrameId !== null) cancelAnimationFrame(animationFrameId);
      animationFrameId = requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          for (const entry of entries) {
            if (entry.target === headerWrapper) {
              document.documentElement.style.setProperty(
                '--header-height',
                `${entry.contentRect.height}px`
              );
            }
          }
        });
      });
    });

    resizeObserver.observe(headerWrapper);

    return () => {
      if (animationFrameId !== null) cancelAnimationFrame(animationFrameId);
      resizeObserver.disconnect();
    };
  }, []);

  return (
    <LazyMotionWrapper>
      <div
        style={{ height: 'var(--header-height)' }}
        className={cn(
          'bg-other-white z-navigation transition-all duration-100 ease-out',
          isCareerPage && 'hidden'
        )}
      >
        <m.header
          animate={controls}
          style={{ top: 'var(--promo-bar-height)' }}
          className="bg-other-white z-navigation fixed right-0 left-0 shadow-sm transition-transform duration-500 ease-out"
          id="header"
          data-test="header"
        >
          {!hidePromoBar && <PromoBar />}
          <HeaderTop />
          <Nav />
          {showUspBar && <UspSection variant="single" />}
        </m.header>
      </div>
    </LazyMotionWrapper>
  );
};

export default Header;
